import Button from "@/components/Button";
import { useMutation } from "@apollo/client";
import { useForm, FormProvider } from "react-hook-form";
import { zodV3Resolver as zodResolver } from "@/lib/zodV3Resolver";
import { z } from "zod/v3";
import { PATCH_FACILITY_MUTATION } from "@/features/facility/queries";
import { EditProps } from "./types";
import FacilityAutocomplete from "@/features/facility/components/ContactDetails/forms/inputs/FacilityAutocomplete";
import { NullableStringSchema } from "./validation";

const ParentCompanySchema = z.object({
  parentCompanyId: NullableStringSchema
});

type FieldValues = z.infer<typeof ParentCompanySchema>;

interface ParentCompanyEditFormProps extends EditProps {
  parentCompany?: { id: string; name: string } | null;
}

const defaultErrorMessage = "Failed to update parent company";

export default function ParentCompanyEditForm({
  facilityId,
  parentCompany: initialParentCompany,
  onCancel,
  onSuccess,
  onError
}: ParentCompanyEditFormProps) {
  const form = useForm<FieldValues>({
    defaultValues: {
      parentCompanyId: initialParentCompany?.id || ""
    },
    resolver: zodResolver(ParentCompanySchema),
    mode: "onChange"
  });

  const [patchFacility, { loading }] = useMutation(PATCH_FACILITY_MUTATION);

  async function onSubmit(values: FieldValues) {
    try {
      const input = {
        id: facilityId,
        ...values
      };

      const { data } = await patchFacility({
        variables: {
          input
        }
      });

      const {
        patchFacility: { success, facility, message }
      } = data;

      if (success) {
        return onSuccess(facility);
      }
      return onError(message || defaultErrorMessage);
    } catch (error) {
      return onError((error as Error).message || defaultErrorMessage);
    }
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6" data-testid="parent-company-edit-form">
        <FacilityAutocomplete
          name="parentCompanyId"
          label="Parent Company"
          initialQuery={initialParentCompany?.name || ""}
        />
        <div className="flex flex-wrap justify-end gap-1 items-center">
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={loading} onClick={form.handleSubmit(onSubmit)}>
            Save
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}
